import { createContext, useContext, useSyncExternalStore, type ComponentType } from "react";
import type { AppState } from "../lib/model";
import { useStoreState } from "../lib/store";
import type { Controller, HoldView } from "./controller";
import type { FakeDaemon } from "../mock/fakeDaemon";
import type { PairRunner } from "../screens/Pair";

export type AppCtx = {
  controller: Controller;
  /** The fake daemon in mock mode (dev builds only); null for a real pairing. */
  mock: FakeDaemon | null;
  pairRunner: (hash: string) => PairRunner;
  /** A development panel rendered over the app, supplied by mock mode; absent in production. */
  devPanel?: ComponentType;
};

export const AppContext = createContext<AppCtx | null>(null);

export function useApp(): AppCtx {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("AppContext missing");
  return ctx;
}

/** The whole state object: it is replaced immutably, so this is a stable snapshot per change. */
export function useAppState(): AppState {
  const { controller } = useApp();
  return useStoreState(controller.store, (s) => s);
}

export function useHold(): HoldView {
  const { controller } = useApp();
  return useSyncExternalStore(controller.hold.subscribe, controller.hold.get, controller.hold.get);
}
